import { useEffect, useState } from "react";
import { X, Upload, Loader2, CheckCircle2, Link as LinkIcon, FileText, Building2, FileUp } from "lucide-react";
import { uploadBankerFile } from "@/lib/banker-upload";
import ProgressLoader from "@/components/progress-loader";

// Standalone "upload an artifact against a portco I cover" modal. Used from
// the banker artifacts index page where there is no task context. Mirrors
// the task-detail page's tabbed file/link UX so the banker has one mental
// model wherever they upload from.
//
// IMPORTANT: This is the link-only/standalone path — the dual-email fan-out
// fires server-side ONLY when `taskId` is also set (i.e. the upload is
// answering an investor request). Standalone deck/note uploads against a
// company are pure record-keeping and stay quiet by design.

const BASE = import.meta.env.BASE_URL;

export default function BankerUploadArtifactModal({ onClose, onUploaded, defaultCompanyId }: {
  onClose: () => void;
  onUploaded: () => void;
  defaultCompanyId?: number | null;
}) {
  const [companies, setCompanies] = useState<any[]>([]);
  const [form, setForm] = useState({
    companyId: defaultCompanyId ? String(defaultCompanyId) : "",
    kind: "zoom_transcript",
    title: "",
    url: "",
    content: "",
  });
  const [tab, setTab] = useState<"file" | "link">("file");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    fetch(`${BASE}api/banker/companies`, { headers: { "x-banker-token": token } })
      .then(r => r.ok ? r.json() : [])
      .then((cos) => setCompanies(cos.filter((c: any) => c.covered)))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.companyId) { setError("Pick a company from your coverage"); return; }
    if (!form.title) { setError("Give it a title"); return; }
    if (tab === "file" && !file) { setError("Choose a file to upload"); return; }
    if (tab === "link" && !form.url && !form.content) { setError("Add a URL or paste the content"); return; }
    setError(null); setUploading(true); setProgress(0);
    const token = localStorage.getItem("banker_token");
    try {
      let filePayload: any = {};
      if (tab === "file" && file) {
        const r = await uploadBankerFile(file, (l, t) => setProgress(Math.min(99, Math.round((l / t) * 100))));
        filePayload = r;
      }
      const r = await fetch(`${BASE}api/banker/artifacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token || "" },
        body: JSON.stringify({
          companyId: parseInt(form.companyId, 10),
          kind: form.kind,
          title: form.title,
          url: tab === "link" ? (form.url || null) : null,
          content: tab === "link" ? (form.content || null) : null,
          ...filePayload,
        }),
      });
      if (r.ok) { onUploaded(); onClose(); }
      else { const e = await r.json().catch(() => ({})); setError(e.error || "Upload failed"); }
    } catch (e: any) { setError(e?.message || "Network error"); }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2"><Upload className="w-5 h-5 text-[#FF5C00]" /> Upload artifact</h2>
            <p className="text-white/50 text-xs mt-1">Log a Zoom transcript, deck, or note against a portfolio company. Meera reads + summarises it for the record.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-red-400 text-xs mb-3">{error}</div>}

        <div className="flex gap-1 border-b border-white/10 mb-3">
          <button onClick={() => setTab("file")} data-testid="standalone-tab-file"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition flex items-center gap-1.5 ${tab === "file" ? "border-[#FF5C00] text-[#FF5C00]" : "border-transparent text-white/50 hover:text-white"}`}>
            <FileUp className="w-3.5 h-3.5" /> Upload file
          </button>
          <button onClick={() => setTab("link")} data-testid="standalone-tab-link"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition flex items-center gap-1.5 ${tab === "link" ? "border-[#FF5C00] text-[#FF5C00]" : "border-transparent text-white/50 hover:text-white"}`}>
            <LinkIcon className="w-3.5 h-3.5" /> Paste link / notes
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-white/60 text-xs mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company (from your coverage)</label>
            <select value={form.companyId} onChange={(e) => setForm(f => ({ ...f, companyId: e.target.value }))}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white">
              <option value="">Select a covered company...</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {companies.length === 0 && <p className="text-[10px] text-amber-400 mt-1">No companies in your coverage. Ask admin to allocate.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Kind</label>
              <select value={form.kind} onChange={(e) => setForm(f => ({ ...f, kind: e.target.value }))}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white">
                <option value="zoom_transcript">Zoom transcript</option>
                <option value="email_thread">Email thread</option>
                <option value="pdf">PDF / document</option>
                <option value="drive_link">Google Drive link</option>
                <option value="note">Meeting note</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Title</label>
              <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Founder call — Q2 numbers"
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
            </div>
          </div>

          {tab === "file" ? (
            <div>
              <label className="text-white/60 text-xs mb-1.5 flex items-center gap-1"><FileUp className="w-3 h-3" /> File (PDF, CSV, XLSX, DOCX, PNG/JPG, TXT — max 25 MB)</label>
              <div className="border border-dashed border-white/20 rounded-lg p-4 bg-white/[0.02]">
                <input type="file" data-testid="standalone-file-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }}
                  accept=".pdf,.csv,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg,.webp,.txt"
                  className="block w-full text-xs text-white/70 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#FF5C00] file:text-white hover:file:bg-[#E55200]" />
                {file && <p className="text-xs text-white/70 mt-2 font-mono">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type || "unknown"}</p>}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-white/60 text-xs mb-1.5 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> URL (Drive / Doc / Recording) — public link, not a local path</label>
                <input value={form.url} onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://..." className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
              </div>
              <div>
                <label className="text-white/60 text-xs mb-1.5 flex items-center gap-1"><FileText className="w-3 h-3" /> Or paste content (transcript / email text)</label>
                <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={7} placeholder="Paste the Zoom transcript, email thread, or meeting notes here. If it's more than a paragraph, Meera will auto-summarise."
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 font-mono leading-relaxed" />
              </div>
            </>
          )}

          {uploading && (
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
              <ProgressLoader active={uploading}
                title={tab === "file" ? `Uploading${progress > 0 && progress < 99 ? ` — ${progress}%` : ""}` : "Saving artifact"}
                stages={tab === "file" ? ["Requesting upload slot", "Streaming bytes to storage", "Recording the artifact", "Asking Meera to summarise"] : ["Recording the artifact", "Asking Meera to summarise"]}
                stageIntervalMs={2500} />
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={submit} disabled={uploading}
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Working...</> : <><CheckCircle2 className="w-4 h-4" /> Save & process</>}
            </button>
            <button onClick={onClose} disabled={uploading} className="text-white/60 hover:text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
